/**
 * Create Organization Modal Component
 *
 * Allows Team tier users to create a new organisation.
 * Enforces business rules:
 * - Only Team tier users can create
 * - Users can only belong to ONE organisation
 * - Minimum 5 seats required
 */

import { useState } from 'react';
import { auth } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';

export default function CreateOrganizationModal({ isOpen, onClose, userProfile }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    seats: 5
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const idToken = await auth.currentUser?.getIdToken();

      const response = await fetch('/api/create-organization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          name: formData.name,
          seats: formData.seats
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create organization');
      }

      // Success! Navigate to organization dashboard
      navigate(`/org/${data.organization.slug}`);
      onClose();
    } catch (error) {
      console.error('Error creating organization:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Check if user is eligible
  if (userProfile?.subscriptionTier !== 'team') {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1c1c1e] rounded-2xl p-8 max-w-md w-full border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">Team Tier Required</h2>
          <p className="text-white/60 mb-6">
            Only users on the Team plan can create organisations.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20"
            >
              Close
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 px-4 py-3 bg-[#06c] text-white rounded-xl hover:bg-[#0055d4]"
            >
              Upgrade to Team
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (userProfile?.organizationId) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-[#1c1c1e] rounded-2xl p-8 max-w-md w-full border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-4">Already in an Organisation</h2>
          <p className="text-white/60 mb-6">
            You can only belong to one organisation at a time. Leave your current organisation before creating a new one.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1c1c1e] rounded-2xl p-8 max-w-md w-full border border-white/10">
        <h2 className="text-2xl font-bold text-white mb-6">Create Organisation</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization Name */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Organisation Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#2c2c2e] text-white rounded-xl border border-white/10 focus:outline-none focus:border-[#06c]"
              placeholder="Acme Inc."
              required
              minLength={2}
              maxLength={100}
            />
          </div>

          {/* Seats */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Number of Seats
            </label>
            <input
              type="number"
              value={formData.seats}
              onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) })}
              className="w-full px-4 py-3 bg-[#2c2c2e] text-white rounded-xl border border-white/10 focus:outline-none focus:border-[#06c]"
              min={5}
              max={1000}
              required
            />
            <p className="text-white/40 text-xs mt-1">
              Minimum 5 seats required for Team tier ($20/seat/month)
            </p>
          </div>

          {/* Pricing Info */}
          <div className="bg-[#2c2c2e] rounded-xl p-4 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/60 text-sm">Monthly Cost:</span>
              <span className="text-white font-bold text-lg">${formData.seats * 20}/mo</span>
            </div>
            <div className="text-white/40 text-xs">
              Billed monthly • Cancel anytime • Prorated billing
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#06c] text-white rounded-xl hover:bg-[#0055d4] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Organisation'}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-white/10">
          <h3 className="text-white font-medium mb-2 text-sm">What happens next?</h3>
          <ul className="space-y-2 text-white/60 text-xs">
            <li>✓ You become the organisation owner</li>
            <li>✓ You can invite team members (up to {formData.seats} seats)</li>
            <li>✓ All members get Team tier features</li>
            <li>✓ Shared recordings, notes, and analytics</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
